import {sumMoney} from './billing.core.js';
import {BILLING_GROUP} from '../vocabulary.js';

/** Key of the supplier view's one-off row; a symbol, so no supplier name can share it. */
export const ONE_OFF_ROW = Symbol('one-off');

/** Name of the one-off row of the supplier view, always the last row. */
const ONE_OFF_ROW_NAME = 'One-off work (not recurring)';

/** Names the part of a total or a year's client income that is one-off. */
export const ONE_OFF_PART_NAME = 'of which one-off (not recurring)';

/**
 * @param {import('../../shell/types/billing.types.js').BillingReportOneOffIncomeApiResponse[]} oneOffIncomes
 * @returns {Map<number, number>} one-off income per year of its invoice date, summed in cents; a year without
 *   one-off income has no entry
 */
export function oneOffIncomePerYear(oneOffIncomes) {
    const perYear = new Map();
    for (const income of oneOffIncomes) {
        const year = Number(income.invoicedOn.slice(0, 4));
        perYear.set(year, sumMoney([perYear.get(year) ?? 0, income.amount]));
    }
    return perYear;
}

/**
 * Invoice dates determine income/cost years; shared platform costs use their period end. One-off incomes count
 * by invoice date and always stay apart from recurring money: in the supplier view as one row of their own, in
 * the client view as a `split` of the client's row, and in `oneOff`, the part of `total` they make up.
 * @param {import('../../shell/types/billing.types.js').BillingReportApiResponse} report
 * @param {number|null} year null selects all years
 * @param {typeof BILLING_GROUP[keyof typeof BILLING_GROUP]} [group]
 * @returns {{rows: object[], total: Totals, oneOff: Totals | null}} amounts summed in cents; `oneOff` is null
 *   when the period has no one-off income
 */
export function reportRows(report, year, group = BILLING_GROUP.SUPPLIER) {
    const inYear = (date) => year === null || Number(date.slice(0, 4)) === year;
    const bySupplier = group === BILLING_GROUP.SUPPLIER;
    const rows = new Map();
    const get = (key, name, extra = {}) => {
        if (!rows.has(key)) rows.set(key, {key, name, invoices: [], platformCosts: [], oneOffIncomes: [], ...extra});
        return rows.get(key);
    };
    const clientRow = (entry) => get(entry.clientSlug, entry.clientName, {slug: entry.clientSlug, deleted: entry.deleted});
    for (const invoice of report.invoices.filter((entry) => inYear(entry.invoicedOn))) {
        const row = bySupplier ? get(invoice.supplier, invoice.supplier ?? 'No supplier') : clientRow(invoice);
        row.invoices.push(invoice);
    }
    for (const cost of report.platformCosts.filter((entry) => inYear(entry.periodTo))) {
        const row = bySupplier ? get(cost.supplier, cost.supplier) : get(null, 'Shared platform costs');
        row.platformCosts.push(cost);
    }
    const oneOffIncomes = report.oneOffIncomes.filter((entry) => inYear(entry.invoicedOn));
    for (const income of oneOffIncomes) {
        const row = bySupplier ? get(ONE_OFF_ROW, ONE_OFF_ROW_NAME) : clientRow(income);
        row.oneOffIncomes.push(income);
    }
    const result = [...rows.values()].map((row) => ({...row, ...rowTotals(row, bySupplier)}));
    result.sort((a, b) => Number(a.key === ONE_OFF_ROW) - Number(b.key === ONE_OFF_ROW) || a.name.localeCompare(b.name));
    return {rows: result, total: {
        income: sumMoney(result.map((row) => row.income)),
        costs: sumMoney(result.map((row) => row.costs)),
        result: sumMoney(result.map((row) => row.result)),
    }, oneOff: oneOffIncomes.length ? oneOffTotals(oneOffIncomes) : null};
}

/** @typedef {{income: number, costs: number, result: number}} Totals */

function oneOffTotals(incomes) {
    return totals(incomes.map((income) => income.amount), incomes.map((income) => income.cost));
}

function totals(amounts, costs) {
    const income = sumMoney(amounts);
    const cost = sumMoney(costs);
    return {income, costs: cost, result: sumMoney([income, -cost])};
}

function rowTotals(row, bySupplier) {
    const recurring = totals(row.invoices.map((invoice) => invoice.amount),
        [...row.invoices.map((invoice) => invoice.cost), ...row.platformCosts.map((cost) => cost.amount)]);
    const oneOff = oneOffTotals(row.oneOffIncomes);
    const split = !bySupplier && row.oneOffIncomes.length > 0 ? {recurring, oneOff} : null;
    return {...totals([recurring.income, oneOff.income], [recurring.costs, oneOff.costs]), split};
}
